import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import type { CampaignSubmission } from '@/api/campaigns/types';
import { getSubmissionLeadPath } from '@/lib/campaignSubmission';

/**
 * Link to the lead or customer a submission created or matched, plus an
 * "Existing customer / lead" badge when the submission was linked to a record
 * that already existed.
 */
const SubmissionLeadLink: React.FC<{
    submission: Pick<CampaignSubmission, 'lead_id' | 'lead' | 'status'>;
    stopPropagation?: boolean;
}> = ({ submission, stopPropagation }) => {
    const { t } = useTranslation('crm');
    const path = getSubmissionLeadPath(submission);

    if (!path) {
        return (
            <span className="text-muted-foreground">
                {t('campaigns.detail.submissions.noLead')}
            </span>
        );
    }

    return (
        <span className="inline-flex flex-wrap items-center gap-2">
            <Link
                to={path}
                className="font-medium text-primary hover:underline"
                onClick={stopPropagation ? event => event.stopPropagation() : undefined}
            >
                {submission.lead?.name ?? t('campaigns.detail.submissions.noLead')}
            </Link>
            {submission.status === 'linked_existing' && (
                <Badge variant="outline" className="font-normal">
                    {t(
                        submission.lead?.type === 'CUSTOMER'
                            ? 'campaigns.detail.submissions.existingCustomer'
                            : 'campaigns.detail.submissions.existingLead'
                    )}
                </Badge>
            )}
        </span>
    );
};

export default SubmissionLeadLink;
